import { useEffect, useRef, useState, useCallback } from "react";
import { Check, Eraser, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Iter AK — Lightweight patient signature capture.
 *
 * Draw → name → save. Saved to `session.signature` and rendered on the PDF.
 *
 * The internals are kept small and dependency-free (no SignaturePad lib) so the
 * surface area stays minimal as we extend later into a full consent-form workflow.
 */
const DEFAULT_ATTESTATION =
  "I acknowledge I received the treatment described in this session and the home-care recommendations.";

function getCanvasCoords(canvas, e) {
  const rect = canvas.getBoundingClientRect();
  const touch = e.touches?.[0] || e.changedTouches?.[0];
  const x = (touch ? touch.clientX : e.clientX) - rect.left;
  const y = (touch ? touch.clientY : e.clientY) - rect.top;
  return { x: (x / rect.width) * canvas.width, y: (y / rect.height) * canvas.height };
}

export default function SignatureCapture({ sessionId, signature, clientName, onSaved, onCleared }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [name, setName] = useState(clientName || "");
  const [editing, setEditing] = useState(!signature);
  const [saving, setSaving] = useState(false);

  // Setup canvas
  useEffect(() => {
    if (!editing) return;
    const c = canvasRef.current;
    if (!c) return;
    // High-DPI: render at 2x for crisp strokes
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(1, 1);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.4 * ratio;
    ctx.strokeStyle = "#1A1A1A";
    ctxRef.current = ctx;
  }, [editing]);

  const start = (e) => {
    e.preventDefault();
    if (!ctxRef.current) return;
    drawingRef.current = true;
    lastPtRef.current = getCanvasCoords(canvasRef.current, e);
  };
  const move = (e) => {
    if (!drawingRef.current || !ctxRef.current) return;
    e.preventDefault();
    const pt = getCanvasCoords(canvasRef.current, e);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPtRef.current = pt;
    if (!hasInk) setHasInk(true);
  };
  const end = () => { drawingRef.current = false; lastPtRef.current = null; };

  const clearCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  }, []);

  const save = async () => {
    if (!name.trim()) { toast.error("Add the printed name first."); return; }
    if (!hasInk) { toast.error("Sign in the box first."); return; }
    if (!sessionId) { toast.error("Save the session draft before signing."); return; }
    setSaving(true);
    try {
      const data_url = canvasRef.current.toDataURL("image/png");
      const { data } = await api.post(`/sessions/${sessionId}/signature`, {
        data_url,
        signer_name: name.trim(),
        kind: "acknowledgement",
        attestation_text: DEFAULT_ATTESTATION,
      });
      onSaved && onSaved(data);
      setEditing(false);
      toast.success("Signature saved.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save signature.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const clearSignature = async () => {
    if (!sessionId) return;
    try {
      await api.delete(`/sessions/${sessionId}/signature`);
      onCleared && onCleared();
      setEditing(true);
      setName(clientName || "");
      toast.success("Signature cleared.");
    } catch {
      toast.error("Could not clear signature.");
    }
  };

  // ── Saved state (read-only with edit affordances) ─────────────────────
  if (!editing && signature) {
    const signedDate = signature.signed_at ? new Date(signature.signed_at) : null;
    const niceDate = signedDate && !isNaN(signedDate.getTime())
      ? signedDate.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
      : "—";
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid="signature-saved">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="label-overline text-sage-700 mb-1">Patient acknowledgement</div>
            <h3 className="font-heading text-lg text-ink-900">Signed by {signature.signer_name}</h3>
            <div className="text-xs text-ink-500 mt-0.5">Signed {niceDate}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { setEditing(true); setName(signature.signer_name || ""); }}
              data-testid="signature-edit-btn"
              className="text-xs text-ink-700 hover:text-ink-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-paper-100"
            >
              <Pencil className="w-3.5 h-3.5" /> Re-sign
            </button>
            <button
              type="button"
              onClick={clearSignature}
              data-testid="signature-clear-btn"
              className="text-xs text-terracotta-600 hover:text-terracotta-700 inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-terracotta-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        </div>
        <div className="bg-paper-50 border border-paper-200 rounded-2xl p-3 flex items-center justify-center">
          <img
            src={signature.data_url}
            alt="Patient signature"
            data-testid="signature-saved-image"
            className="max-h-24 w-auto"
          />
        </div>
        {signature.attestation_text && (
          <p className="text-xs text-ink-500 italic mt-3">{signature.attestation_text}</p>
        )}
      </div>
    );
  }

  // ── Editing state (canvas + name + actions) ───────────────────────────
  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid="signature-capture">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-overline text-sage-700 mb-1">Patient acknowledgement</div>
          <h3 className="font-heading text-xl text-ink-900">Sign &amp; acknowledge today&rsquo;s session</h3>
          <p className="text-sm text-ink-600 mt-1 max-w-lg">{DEFAULT_ATTESTATION}</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label-overline block mb-2">Printed name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="signature-name-input"
            placeholder="Type your full name"
            className="w-full px-4 py-2.5 rounded-2xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
            maxLength={120}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label-overline">Signature</label>
            <button
              type="button"
              onClick={clearCanvas}
              data-testid="signature-clear-canvas-btn"
              className="text-xs text-ink-500 hover:text-ink-900 inline-flex items-center gap-1"
            >
              <Eraser className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
          <div className="bg-paper-50 border-2 border-dashed border-paper-300 rounded-2xl">
            <canvas
              ref={canvasRef}
              data-testid="signature-canvas"
              className="w-full block touch-none rounded-2xl"
              style={{ height: 160, cursor: "crosshair" }}
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
            />
          </div>
          <p className="text-xs text-ink-500 mt-2">Sign with a finger on touchscreen, or trackpad/mouse on desktop. Date &amp; time will be stamped automatically.</p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          {signature && (
            <button
              type="button"
              onClick={() => setEditing(false)}
              data-testid="signature-cancel-btn"
              className="min-h-[44px] px-5 rounded-full text-ink-700 hover:bg-paper-100 text-sm"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={clearCanvas}
            data-testid="signature-restart-btn"
            className="min-h-[44px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm inline-flex items-center gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restart
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !hasInk || !name.trim() || !sessionId}
            data-testid="signature-save-btn"
            className="min-h-[44px] px-5 rounded-full bg-ink-900 hover:bg-ink-800 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            {saving ? "Saving…" : "Save signature"}
          </button>
        </div>
      </div>
    </div>
  );
}
